import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Wallet, TrendingUp, Circle, Check, ChevronLeft, ChevronRight, Info, IndianRupee } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Link } from "react-router-dom";
import { formatINR } from "@/lib/currency";
import { formatDate } from "@/lib/datetime";

const money = (n) => formatINR(n);
const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const METHODS = ["BANK_TRANSFER", "CASH", "UPI", "CHEQUE"];

function toMonth(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
function fromMonth(m) {
  const [y, mo] = m.split("-").map(Number);
  return new Date(y, mo - 1, 1);
}

export default function Payroll() {
  const [month, setMonth] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return toMonth(d);
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payOpen, setPayOpen] = useState(false);
  const [payRow, setPayRow] = useState(null);
  const [payMethod, setPayMethod] = useState("Bank Transfer");
  const [payAmount, setPayAmount] = useState("");
  const [payReference, setPayReference] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get(`/payroll?month=${month}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load payroll"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [month]);// eslint-disable-line

  const nav = (delta) => {
    const d = fromMonth(month);
    d.setMonth(d.getMonth() + delta);
    setMonth(toMonth(d));
  };

  const monthLabel = useMemo(() => {
    const d = fromMonth(month);
    return `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`;
  }, [month]);

  // Total calendar days in the selected month (e.g. Feb 2026 -> 28).
  const totalCalendarDays = useMemo(() => {
    const [y, mo] = month.split("-").map(Number);
    return new Date(y, mo, 0).getDate();
  }, [month]);

  const openPay = (row) => {
    setPayRow(row);
    // For an already-paid row (re-mark), prefill with the recorded values so a
    // mistaken payment can be corrected; otherwise default to the payable amount.
    setPayMethod(row.paidMethod || "BANK_TRANSFER");
    setPayAmount(String(row.finalPayable ?? row.totalPayable));
    setPayReference(row.paidReference || "");
    setPayOpen(true);
  };

  const confirmPay = async () => {
    if (!payRow) return;
    const amt = Number(payAmount);
    if (isNaN(amt) || amt <= 0) { toast.error("Enter a valid amount"); return; }
    setSaving(true);
    try {
      await api.put("/payroll/mark-paid", {
        staffId: payRow.staffId,
        month,
        amount: amt,
        method: payMethod,
        reference: payReference,
      });
      toast.success(payRow.status === "paid" ? `Updated payment for ${payRow.name}` : `Marked ${payRow.name} as paid`);
      setPayOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to mark paid");
    } finally {
      setSaving(false);
    }
  };

  const summary = data?.summary || { totalBase: 0, totalCommission: 0, totalPayable: 0, totalPaid: 0, outstanding: 0 };
  const rows = data?.rows || [];

  return (
    <div className="space-y-8" data-testid="payroll-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="overline mb-2">Compensation</div>
          <h1 className="font-heading text-4xl text-white inline-flex items-center gap-3">
            <IndianRupee className="w-8 h-8 text-emerald-500" /> Payroll
          </h1>
          <p className="text-emerald-muted mt-2 text-sm">Monthly payouts calculated from staff base salary + commission on sales revenue.</p>
        </div>
        <div className="flex items-center gap-2 bg-emerald-surface border border-gold-subtle rounded-full px-2 py-1.5">
          <button onClick={() => nav(-1)} data-testid="prev-month-btn" className="p-1.5 rounded-full text-emerald-muted hover:text-gold hover:bg-emerald-elev/60"><ChevronLeft className="w-4 h-4" /></button>
          <div className="min-w-[130px] text-center text-white text-sm font-medium" data-testid="current-month">{monthLabel}</div>
          <button onClick={() => nav(1)} data-testid="next-month-btn" className="p-1.5 rounded-full text-emerald-muted hover:text-gold hover:bg-emerald-elev/60"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="payroll-summary">
        <div className="bg-emerald-surface border border-gold-subtle rounded-2xl p-5">
          <div className="overline">Total Payable</div>
          <div className="font-heading text-3xl text-white mt-2">{money(summary.totalPayable)}</div>
          <div className="text-xs text-emerald-muted mt-1">Base {money(summary.totalBase)} + Commission {money(summary.totalCommission)}</div>
        </div>
        <div className="bg-emerald-surface border border-gold-subtle rounded-2xl p-5">
          <div className="overline">Paid</div>
          <div className="font-heading text-3xl text-white mt-2" data-testid="summary-paid">{money(summary.totalPaid)}</div>
          <div className="text-xs text-emerald-muted mt-1">Across {rows.filter((r) => r.status === "paid").length} staff</div>
        </div>
        <div className="bg-emerald-surface border border-gold-subtle rounded-2xl p-5">
          <div className="overline">Outstanding</div>
          <div className="font-heading text-3xl text-gold mt-2" data-testid="summary-outstanding">{money(summary.outstanding)}</div>
          <div className="text-xs text-emerald-muted mt-1">{rows.filter((r) => r.status === "pending").length} staff pending</div>
        </div>
        <div className="bg-emerald-surface border border-gold-subtle rounded-2xl p-5">
          <div className="overline">Commission Rate</div>
          <div className="font-heading text-3xl text-white mt-2 flex items-center gap-2"><TrendingUp className="w-5 h-5 text-gold" /> {summary.totalBase > 0 ? Math.round((summary.totalCommission / summary.totalBase) * 100) : 0}%</div>
          <div className="text-xs text-emerald-muted mt-1">Commission ÷ Base</div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-emerald-surface border border-gold-subtle rounded-2xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="thead-row hover:bg-[rgba(197,160,89,0.06)]">
              <TableHead className="th-gold text-center w-12">Info</TableHead>
              <TableHead className="th-gold">Staff</TableHead>
              <TableHead className="th-gold text-right">Days Worked</TableHead>
              <TableHead className="th-gold text-right">Revenue</TableHead>
              <TableHead className="th-gold text-right">Base</TableHead>
              <TableHead className="th-gold text-right whitespace-nowrap">Comm %</TableHead>
              <TableHead className="th-gold text-right">Comm</TableHead>
              <TableHead className="th-gold text-right">Bonus</TableHead>
              <TableHead className="th-gold text-right">Adjustment</TableHead>
              <TableHead className="th-gold text-right">Payable</TableHead>
              <TableHead className="th-gold text-center w-28">Status</TableHead>
              <TableHead className="th-gold text-right w-40">Payment</TableHead>
              <TableHead className="th-gold text-center w-28">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={13} className="text-center text-emerald-muted py-8">Calculating…</TableCell></TableRow>}
            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={13} className="text-center text-emerald-muted py-8">
                  No staff yet. <Link to="/dashboard/staff" className="text-gold hover:underline">Add staff</Link> and set <Link to="/dashboard/salary-rules" className="text-gold hover:underline">salary rules</Link> first.
                </TableCell>
              </TableRow>
            )}
            {!loading && rows.map((r) => (
              <TableRow key={r.staffId} className="border-gold-subtle" data-testid={`payroll-row-${r.staffId}`}>
                <TableCell className="text-center">
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        data-testid={`payroll-breakdown-${r.staffId}`}
                        title="Calculation snapshot"
                        className="shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full bg-[rgba(197,160,89,0.15)] text-gold border border-gold-subtle hover:bg-gold hover:text-emerald-deep transition">
                        <Info className="w-3.5 h-3.5 text-cyan-500"  />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent align="start" className="w-64 bg-gray-900 border-gold-subtle text-white">
                        <div className="text-sm font-medium text-white mb-2 pb-2 border-b border-gold-subtle">{r.name} · {monthLabel}</div>
                        <div className="space-y-1.5 text-xs">
                          <div className="flex justify-between"><span className="text-amber-200">Base Salary</span><span className="text-white">{money(r.baseSalary)}</span></div>
                          <div className="flex justify-between"><span className="text-amber-200">Total Calendar Days</span><span className="text-white">{totalCalendarDays}</span></div>
                          <div className="flex justify-between"><span className="text-amber-200">Leave Days</span><span className="text-white">{r.leaveDays ?? 0}</span></div>
                          <div className="flex justify-between"><span className="text-amber-200">Days Worked</span><span className="text-white">{r.workingDays ?? "—"}</span></div>
                          <div className="flex justify-between"><span className="text-amber-200">Appointments</span><span className="text-white">{r.appointments ?? 0}</span></div>
                          <div className="flex justify-between"><span className="text-amber-200">Commission ({r.commissionPct}%)</span><span className="text-white">{money(r.commissionEarned)}</span></div>
                          <div className="flex justify-between"><span className="text-amber-200">Bonus</span><span className="text-white">{money(r.bonusAmount || 0)}</span></div>
                          <div className="flex justify-between"><span className="text-amber-200">LeaveDeduction</span><span className="text-white">{money(r.leaveDeduction || 0)}</span></div>
                          <div className="flex justify-between"><span className="text-amber-200">Payable</span><span className="text-white">{money(r.finalPayable ?? r.totalPayable)}</span></div>
                          <div className="flex justify-between"><span className="text-amber-200">Adjustment</span><span className="text-white">{money(r.adjustment || 0)}</span></div>
                          <div className="flex justify-between border-t border-gold-subtle pt-1.5 mt-1.5">
                            <span className="text-amber-200">Final Paid Amount</span>
                            <span className="text-gold font-medium">{money(r.paidAmount ?? 0)}</span>
                          </div>
                        </div>
                      </PopoverContent>
                    </Popover>
                </TableCell>
                <TableCell>
                  <span className="text-white font-medium">{r.name}</span>
                </TableCell>
                <TableCell className="text-right text-emerald-muted">{r.workingDays ?? "—"}</TableCell>
                <TableCell className="text-right text-emerald-muted">{money(r.revenueGenerated)}</TableCell>
                <TableCell className="text-right text-white">{money(r.baseSalary)}</TableCell>
                <TableCell className="text-right text-emerald-muted">{r.commissionPct}%</TableCell>
                <TableCell className="text-right text-white">{money(r.commissionEarned)}</TableCell>
                <TableCell className="text-right text-white">{money(r.bonusAmount || 0)}</TableCell>
                <TableCell className="text-right text-white">{money(r.adjustment || 0)}</TableCell>
                <TableCell className="text-right text-gold font-medium">{money(r.finalPayable ?? r.totalPayable)}</TableCell>
                <TableCell className="text-center">
                  {r.status === "paid"
                    ? <Badge variant="outline" className="border-green-800 text-green-300 bg-[rgba(46,125,50,0.15)]"><Check className="w-3 h-3 mr-1" /> Paid</Badge>
                    : <Badge variant="outline" className="border-gold-subtle text-gold bg-[rgba(197,160,89,0.1)]"><Circle className="w-2.5 h-2.5 mr-1 fill-current" /> Pending</Badge>}
                </TableCell>
                <TableCell className="text-right text-[11px] text-emerald-muted">
                  {r.status === "paid" ? (
                    <>{r.paidMethod}<br/>{formatDate(r.paidAt)}</>
                  ) : "—"}
                </TableCell>
                <TableCell className="text-center">
                  {r.status === "pending" ? (
                    <button
                      onClick={() => openPay(r)}
                      data-testid={`mark-paid-${r.staffId}`}
                      className="gold-button inline-flex items-center gap-1.5 px-2.5 h-7 rounded-md text-xs whitespace-nowrap"
                    >
                      <Wallet className="w-3 h-3" /> Mark Paid
                    </button>
                  ) : (
                    <button
                      onClick={() => openPay(r)}
                      data-testid={`re-mark-${r.staffId}`}
                      className="gold-outline-button inline-flex items-center gap-1.5 px-2.5 h-7 rounded-md text-xs whitespace-nowrap"
                    >
                      <Wallet className="w-3 h-3" /> Edit Amount
                    </button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Mark Paid Modal */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="bg-emerald-surface border-gold-subtle text-white max-w-md" data-testid="mark-paid-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">{payRow?.status === "paid" ? "Re-mark payment" : "Mark as paid"}</DialogTitle>
            <DialogDescription className="text-emerald-muted">
              {payRow ? `${payRow.name} · ${monthLabel}` : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Amount (₹)</Label>
              <Input type="number" min="0" step="0.01" value={payAmount}
                onChange={(e) => setPayAmount(e.target.value)}
                className="bg-emerald-elev border-gold-subtle mt-1" data-testid="pay-amount-input" />
            </div>
            <div>
              <Label>Method</Label>
              <Select value={payMethod} onValueChange={setPayMethod}>
                <SelectTrigger className="bg-emerald-elev border-gold-subtle mt-1" data-testid="pay-method-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-emerald-surface border-gold-subtle text-white">
                  {METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Reference / Txn ID (optional)</Label>
              <Input value={payReference} onChange={(e) => setPayReference(e.target.value)}
                className="bg-emerald-elev border-gold-subtle mt-1" data-testid="pay-reference-input" />
            </div>
            <button onClick={confirmPay} disabled={saving}
              className="gold-button w-full h-11 rounded-md disabled:opacity-60"
              data-testid="confirm-pay-btn">
              {saving ? "Recording…" : "Record Payment"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
